import { Client, IMessage, StompSubscription } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import type {
  TableUpdateMessage,
  MenuUpdateMessage,
  PosHeartbeatMessage,
} from '@/types/api';

type MessageHandler<T> = (message: T) => void;

interface Subscription {
  id: string;
  destination: string;
  onMessage: (message: IMessage) => void;
  stomp?: StompSubscription;
}

class WebSocketService {
  private client: Client | null = null;
  private connected = false;
  private connectPromise: Promise<void> | null = null;
  private connectResolve: (() => void) | null = null;
  private connectReject: ((error: any) => void) | null = null;
  private reconnectDelay = 3000;
  // Keyed by a unique id (not destination) so multiple components can subscribe to
  // the same topic, and so subscriptions can be re-applied after a reconnect.
  private subscriptions = new Map<string, Subscription>();
  private idSeq = 0;

  connect(): Promise<void> {
    // Already connected with a live client.
    if (this.connected && this.client?.connected) {
      return Promise.resolve();
    }

    // An in-flight connect attempt — reuse its promise.
    if (this.connectPromise) {
      return this.connectPromise;
    }

    // After a failed first connect, connectPromise is null but the client may
    // still be auto-reconnecting (stompjs reconnectDelay). Don't create a second
    // client — attach a fresh promise that resolves when the existing client
    // eventually connects. (H2 fix: prevents zombie/orphaned connections.)
    if (this.client?.active) {
      this.connectPromise = new Promise<void>((resolve, reject) => {
        this.connectResolve = resolve;
        this.connectReject = reject;
      });
      return this.connectPromise;
    }

    // No active client — clean up any dead one before creating a new client.
    if (this.client) {
      try { this.client.deactivate(); } catch { /* ignore */ }
      this.client = null;
    }

    this.connectPromise = new Promise<void>((resolve, reject) => {
      this.connectResolve = resolve;
      this.connectReject = reject;

      const client = new Client({
        // Same-origin deployment: Spring Boot serves the SockJS endpoint at /ws
        // on the same origin as the dashboard. In dev, Vite proxies /ws →
        // localhost:8080. Keeping this relative avoids cross-origin/CORS config.
        webSocketFactory: () => new SockJS('/ws'),
        connectHeaders: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        // H3 fix: Refresh the token before every connect/reconnect attempt so
        // the STOMP layer never authenticates with a stale JWT.
        beforeConnect: () => {
          const freshToken = localStorage.getItem('accessToken');
          if (freshToken) {
            client.connectHeaders = {
              Authorization: `Bearer ${freshToken}`,
            };
          }
        },
        debug: () => {},
        // Let stompjs auto-reconnect the SAME client; onConnect re-applies subscriptions.
        reconnectDelay: this.reconnectDelay,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
        onConnect: () => {
          this.connected = true;
          this.applyAllSubscriptions();
          if (this.connectResolve) {
            this.connectResolve();
          }
          this.clearPendingConnect();
        },
        onStompError: (frame) => {
          if (this.connectReject) {
            this.connectReject(new Error(frame.headers['message']));
          }
          // H2 fix: null out connectPromise so future connect() calls can retry
          // instead of receiving the same rejected promise forever.
          this.clearPendingConnect();
        },
        onWebSocketClose: () => {
          this.connected = false;
          // Stale STOMP subscriptions must be re-created on the next connect.
          this.subscriptions.forEach((sub) => {
            sub.stomp = undefined;
          });
        },
        onWebSocketError: (error) => {
          if (this.connectReject) {
            this.connectReject(error);
          }
          // H2 fix: same as onStompError — allow retry on next connect().
          this.clearPendingConnect();
        },
      });

      this.client = client;
      client.activate();
    });

    return this.connectPromise;
  }

  /** Clear the pending connect promise + resolvers (H2 fix). */
  private clearPendingConnect(): void {
    this.connectPromise = null;
    this.connectResolve = null;
    this.connectReject = null;
  }

  /**
   * Deactivate the current connection and establish a fresh one.
   * Used when the auth token changes (H3) to ensure the STOMP layer
   * authenticates with the new token immediately rather than waiting
   * for an auto-reconnect.
   */
  forceReconnect(): void {
    if (this.client) {
      try { this.client.deactivate(); } catch { /* ignore */ }
      this.client = null;
    }
    this.connected = false;
    this.clearPendingConnect();
    // Subscriptions persist in the map; they'll be re-applied on the next connect.
    this.subscriptions.forEach((sub) => {
      sub.stomp = undefined;
    });
    // Fire-and-forget — callers don't need to await this.
    this.connect().catch(() => { /* auto-reconnect will keep trying */ });
  }

  disconnect(): void {
    this.subscriptions.forEach((sub) => {
      if (sub.stomp) {
        try { sub.stomp.unsubscribe(); } catch { /* ignore */ }
      }
    });
    this.subscriptions.clear();
    if (this.client) {
      this.client.deactivate();
      this.client = null;
    }
    this.connected = false;
    this.clearPendingConnect();
  }

  isConnected(): boolean {
    return this.connected && this.client?.connected === true;
  }

  /** (Re)apply every registered subscription to the current client. */
  private applyAllSubscriptions(): void {
    if (!this.client || !this.connected) {
      return;
    }
    this.subscriptions.forEach((sub) => {
      if (!sub.stomp) {
        sub.stomp = this.client!.subscribe(sub.destination, sub.onMessage);
      }
    });
  }

  /**
   * Register a subscription spec. Applied immediately if connected, otherwise picked up
   * by applyAllSubscriptions() on the next (re)connect. Returns an unsubscribe function.
   */
  private register<T>(destination: string, handler: MessageHandler<T>): () => void {
    const id = `${destination}#${++this.idSeq}`;
    const onMessage = (message: IMessage) => {
      try {
        handler(JSON.parse(message.body) as T);
      } catch {
        // Silently ignore malformed messages
      }
    };
    const sub: Subscription = { id, destination, onMessage };
    this.subscriptions.set(id, sub);

    if (this.client && this.connected) {
      sub.stomp = this.client.subscribe(destination, onMessage);
    }

    return () => {
      const existing = this.subscriptions.get(id);
      if (existing?.stomp) {
        try { existing.stomp.unsubscribe(); } catch { /* ignore */ }
      }
      this.subscriptions.delete(id);
    };
  }

  // Subscribe to table updates for a specific restaurant
  subscribeToTableUpdates(
    restaurantId: string,
    handler: MessageHandler<TableUpdateMessage>
  ): () => void {
    return this.register(`/topic/tables/${restaurantId}`, handler);
  }

  // Subscribe to menu updates for a specific restaurant
  subscribeToMenuUpdates(
    restaurantId: string,
    handler: MessageHandler<MenuUpdateMessage>
  ): () => void {
    return this.register(`/topic/menu/${restaurantId}`, handler);
  }

  // Subscribe to config updates for a specific restaurant
  subscribeToConfigUpdates(
    restaurantId: string,
    handler: MessageHandler<any>
  ): () => void {
    return this.register(`/topic/config/${restaurantId}`, handler);
  }

  // Subscribe to inventory updates for a specific restaurant
  subscribeToInventory(
    restaurantId: string,
    handler: MessageHandler<any>
  ): () => void {
    return this.register(`/topic/inventory/${restaurantId}`, handler);
  }

  // Subscribe to POS terminal heartbeats for a specific restaurant
  subscribeToHeartbeats(
    restaurantId: string,
    handler: MessageHandler<PosHeartbeatMessage>
  ): () => void {
    return this.register(`/topic/pos-heartbeat/${restaurantId}`, handler);
  }

  // Subscribe to all updates for a restaurant
  subscribeToRestaurant(restaurantId: string, handlers: {
    onTableUpdate?: MessageHandler<TableUpdateMessage>;
    onMenuUpdate?: MessageHandler<MenuUpdateMessage>;
    onConfigUpdate?: MessageHandler<any>;
  }): () => void {
    const unsubscribers: Array<() => void> = [];

    if (handlers.onTableUpdate) {
      unsubscribers.push(
        this.subscribeToTableUpdates(restaurantId, handlers.onTableUpdate)
      );
    }

    if (handlers.onMenuUpdate) {
      unsubscribers.push(
        this.subscribeToMenuUpdates(restaurantId, handlers.onMenuUpdate)
      );
    }

    if (handlers.onConfigUpdate) {
      unsubscribers.push(
        this.subscribeToConfigUpdates(restaurantId, handlers.onConfigUpdate)
      );
    }

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }
}

// Export appropriate service based on environment
const useMockApi = import.meta.env.VITE_USE_MOCK_API === 'true';

let service: WebSocketService;

if (useMockApi) {
  // Dynamically import mock service
  const { mockWebSocketService } = await import('./mockWebSocket');
  service = mockWebSocketService as unknown as WebSocketService;
} else {
  service = new WebSocketService();
}

export const websocketService = service;
export default websocketService;
